import { Box, Paper, Link, Stack, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArticleMeta from '../../types/ArticleMeta';
import { ArticleCardLocation } from '../../utilities/gtm';
import ArticleCarouselSkeleton from '../Article/ArticleCarouselSkeleton';
import ArticleCarousel from '../Article/ArticleCarousel';
import { Link as RouterLink } from 'react-router';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { useTranslation } from 'react-i18next';
import { apiStates } from '../../types/types';
import { useMemo, useState } from 'react';
import ArticleListSkeleton from '../Article/ArticleListSkeleton';
import ArticleList from '../Article/ArticleList';
import { DividerWithContent } from '../../common/components/DividerWithContent';

interface LoadingViewProps {
  variant: 'grid' | 'carousel';
  limit: number;
}

const LoadingView = ({
  variant = 'grid',
  limit,
}: Readonly<LoadingViewProps>) => {
  if (variant === 'carousel') {
    return (
      <Box
        sx={{
          marginX: {
            xs: -2,
            lg: 0,
          },
        }}
      >
        <ArticleCarouselSkeleton
          limit={limit}
          carouselListStyle={{
            paddingX: 2,
            scrollPadding: 16,
          }}
        />
      </Box>
    );
  }

  return (
    <ArticleListSkeleton
      cardVariant='highlight'
      limit={limit}
      showGrid
      columns={3}
    />
  );
};

const EndCard = ({ route }: { route: string }) => {
  const { t } = useTranslation();

  return (
    <Paper
      component={Stack}
      variant='outlined'
      direction='row'
      justifyContent='center'
      alignItems='center'
      sx={{ width: '100%', height: '100%' }}
    >
      <Link
        component={RouterLink}
        to={route}
        underline='hover'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
        variant='h3'
      >
        <ArrowCircleRightOutlinedIcon sx={{ width: '56px', height: '56px' }} />
        {t('frontPage.tabs.latest.cta')}
      </Link>
    </Paper>
  );
};

const minimizedGridArticleCount = 3;

interface GridViewProps {
  articles: ArticleMeta[];
  location: ArticleCardLocation;
  route?: string;
}

function GridView({ articles, location, route }: Readonly<GridViewProps>) {
  const { t } = useTranslation();

  const [isGridMinimized, setIsGridMinimized] = useState<boolean>(true);

  function openGrid() {
    setIsGridMinimized(false);
  }

  const visibleArticles = useMemo(() => {
    return isGridMinimized
      ? articles.slice(0, minimizedGridArticleCount)
      : articles;
  }, [isGridMinimized, articles]);

  return (
    <>
      <ArticleList
        articles={visibleArticles}
        heading={'h3'}
        location={location}
        cardVariant='highlight'
        showGrid
        columns={3}
      />
      {isGridMinimized && articles.length > minimizedGridArticleCount && (
        <Stack
          sx={{
            marginTop: 1.5,
          }}
        >
          <DividerWithContent>
            <Button
              variant='text'
              onClick={openGrid}
              endIcon={<ExpandMoreIcon />}
            >
              {t('frontPage.tabs.latest.showMore')}
            </Button>
          </DividerWithContent>
        </Stack>
      )}
      {route && !isGridMinimized && (
        <Stack
          sx={{
            marginTop: 1.5,
          }}
        >
          <DividerWithContent>
            <Button
              variant='contained'
              component={RouterLink}
              to={route}
              endIcon={<ArrowForwardOutlinedIcon />}
            >
              {t('frontPage.tabs.latest.cta')}
            </Button>
          </DividerWithContent>
        </Stack>
      )}
    </>
  );
}

interface TabPanelContentAreaProps {
  state: string;
  articles: ArticleMeta[];
  articleLimit: number;
  location: ArticleCardLocation;
  route?: string;
  variant?: 'grid' | 'carousel';
  emptyView?: React.ReactNode;
}

export default function TabPanelContentArea({
  state,
  articles,
  articleLimit,
  route,
  location,
  emptyView = <></>,
  variant = 'grid',
}: Readonly<TabPanelContentAreaProps>) {
  if (state === apiStates.FETCHING) {
    return (
      <LoadingView
        variant={variant}
        limit={variant === 'grid' ? minimizedGridArticleCount : articleLimit}
      />
    );
  }

  if (state === apiStates.READY && articles.length > 0) {
    return (
      <>
        {variant === 'grid' && (
          <GridView articles={articles} location={location} route={route} />
        )}
        {variant === 'carousel' && (
          <Box
            sx={{
              marginX: {
                xs: -2,
                lg: 0,
              },
            }}
          >
            <ArticleCarousel
              articles={articles}
              location={location}
              headingLevel='h3'
              carouselListStyle={{
                paddingX: 2,
                scrollPadding: 16,
              }}
              stepperAreaStyle={{
                paddingX: 2,
              }}
              endComponent={route ? <EndCard route={route} /> : undefined}
            />
          </Box>
        )}
      </>
    );
  }

  if (state === apiStates.READY && articles.length === 0) {
    return emptyView;
  }

  return <></>;
}
